import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';
const KEY = 'halvard.motion';
const EVENT = 'halvard:motion';

/** `auto` follows the system's reduce-motion setting; `on` and `off` override it. */
export type MotionPreference = 'auto' | 'on' | 'off';

export function resolveReducedMotion(
  preference: MotionPreference,
  systemReduces: boolean,
): boolean {
  return preference === 'auto' ? systemReduces : preference === 'off';
}

export function motionPreference(): MotionPreference {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'on' || stored === 'off' ? stored : 'auto';
  } catch {
    return 'auto';
  }
}

/** Saves the choice for this browser and tells every board to follow it. */
export function setMotionPreference(preference: MotionPreference): void {
  try {
    if (preference === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, preference);
  } catch {
    // Storage blocked: the choice lasts until the page closes.
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: preference }));
}

function systemReduces(): boolean {
  return window.matchMedia(QUERY).matches;
}

/** The player's animation choice and what the system asks for. */
export function useMotionSettings(): {
  preference: MotionPreference;
  systemReduces: boolean;
  reduced: boolean;
} {
  const [preference, setPreference] = useState(motionPreference);
  const [system, setSystem] = useState(systemReduces);
  useEffect(() => {
    const media = window.matchMedia(QUERY);
    const onSystem = () => setSystem(media.matches);
    const onChoice = (event: Event) =>
      setPreference((event as CustomEvent<MotionPreference>).detail);
    media.addEventListener('change', onSystem);
    window.addEventListener(EVENT, onChoice);
    return () => {
      media.removeEventListener('change', onSystem);
      window.removeEventListener(EVENT, onChoice);
    };
  }, []);
  return { preference, systemReduces: system, reduced: resolveReducedMotion(preference, system) };
}

/** Whether to skip animations: the system's setting, unless the player chose otherwise. */
export function useReducedMotion(): boolean {
  return useMotionSettings().reduced;
}
